/**
 * lib/requisitions.ts
 * ------------------------------------------------------------------
 * MAY WE BUY THIS — asked before the money is committed, not after.
 *
 * The chain this completes runs:
 *
 *   REQUISITION  may we buy it            (this file)
 *   ORDER        we have told the supplier (lib/purchaseOrders.ts)
 *   RECEIPT      it arrived, this much of it
 *   VOUCHER      pay for it               (lib/vouchers.ts)
 *
 * Every document after the first one records something that has already
 * happened. The requisition is the only point where the answer can still
 * be no, which is why it is worth the two minutes it costs a teacher.
 *
 * TWO APPROVALS, AND THEY ANSWER DIFFERENT QUESTIONS. The named approver
 * — a head of department, whoever the requester nominates — answers "does
 * this department need this". Operations answers "can the school afford
 * it, and is this how we spend". One person cannot answer both, because
 * the second question is only meaningful from outside the department
 * asking.
 *
 * APPROVAL IS A ROLE, NEVER A PERSON. Whoever holds operationsAdmin
 * approves. Naming an individual anywhere in here would mean a school
 * that cannot buy a textbook while one person is on leave, and a code
 * change every time the post is filled by somebody new.
 *
 * NOBODY APPROVES THEIR OWN REQUEST, the same distinct-actor rule the
 * vouchers carry, and for the same reason: somebody will eventually hold
 * two of these capabilities for a perfectly good reason, and that is
 * precisely when the guard has to hold.
 *
 * EVENT-SOURCED. Every transition is an append-only event; `currentStatus`
 * is the denormalised view lists and rules read. The history is the
 * evidence, and a status that can be overwritten is not evidence.
 *
 * PURE. A requisition and an actor in, an answer out.
 * ------------------------------------------------------------------ */

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export const REQ_COLLECTIONS = {
  requisitions: 'financeRequisitions',
  /** Events live in the subcollection `financeRequisitions/{id}/events`. */
  eventsSub: 'events',
  counters: 'financeCounters',
} as const;

export const reqCounterId = (year: string): string => `requisitions-${year}`;
export const formatReqNo = (seq: number, year: string): string =>
  `REQ/${year}/${String(seq).padStart(4, '0')}`;

/* ================================ types ================================ */

export type ReqStatus =
  | 'draft'
  | 'submitted'
  | 'approver_approved'
  | 'operations_approved'
  | 'ordered'
  | 'closed'
  | 'rejected';

export const REQ_STATUS_LABEL: Record<ReqStatus, string> = {
  draft: 'Draft',
  submitted: 'Awaiting the approver',
  approver_approved: 'Awaiting operations',
  operations_approved: 'Approved — ready to order',
  ordered: 'Ordered',
  closed: 'Closed',
  rejected: 'Rejected',
};

export const REQ_TERMINAL: ReqStatus[] = ['closed', 'rejected'];

export interface ReqLine {
  description: string;
  qty: number;
  estUnitCost?: number;
  estLineTotal?: number;
}

export interface Requisition {
  id: string;
  reqNo: string;
  title: string;
  department?: string;
  /** Why the school should spend this. The field the approver actually reads. */
  justification?: string;
  lines: ReqLine[];
  /** An ESTIMATE, and labelled as one everywhere. The real figure is the
   *  order, and treating a requisition total as a commitment is how a
   *  budget report comes to disagree with the ledger. */
  estTotal: number;
  requestedBy: string;
  requestedByName?: string;
  /** The nominated approver's uid — the reliable routing key. */
  approverUid?: string;
  /** Their teacher record, so it still resolves if the login was linked
   *  after the requisition was raised. */
  approverId?: string;
  approverName?: string;
  currentStatus: ReqStatus;
  submittedAt?: string;
  approverApprovedAt?: string;
  approverApprovedBy?: string;
  operationsApprovedAt?: string;
  operationsApprovedBy?: string;
  operationsApprovedByName?: string;
  /** The order raised from this requisition, once there is one. */
  poId?: string;
  poNo?: string;
  orderedAt?: string;
  closedAt?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  rejectedReason?: string;
  createdAt: string;
  updatedAt?: string;
}

export type ReqEventType =
  | 'created' | 'submitted' | 'approver_approved' | 'operations_approved'
  | 'ordered' | 'closed' | 'rejected' | 'comment';

export const REQ_EVENT_VERB: Record<ReqEventType, string> = {
  created: 'Raised',
  submitted: 'Submitted for approval',
  approver_approved: 'Approved by the department',
  operations_approved: 'Approved by operations',
  ordered: 'Order raised',
  closed: 'Closed',
  rejected: 'Rejected',
  comment: 'Comment added',
};

export interface ReqEvent {
  id: string;
  type: ReqEventType;
  /** The status it moved TO. */
  status?: ReqStatus;
  by?: string;
  byName?: string;
  at: string;
  comment?: string;
}

/* ============================ the state machine ============================ */

export type ReqAction =
  | 'submit' | 'approverApprove' | 'operationsApprove' | 'order' | 'close' | 'reject';

export interface ReqActionDef {
  key: ReqAction;
  label: string;
  from: ReqStatus;
  to: ReqStatus;
  eventType: ReqEventType;
}

export const REQ_ACTIONS: ReqActionDef[] = [
  { key: 'submit', label: 'Submit for approval', from: 'draft', to: 'submitted', eventType: 'submitted' },
  { key: 'approverApprove', label: 'Approve', from: 'submitted', to: 'approver_approved', eventType: 'approver_approved' },
  { key: 'operationsApprove', label: 'Approve for purchase', from: 'approver_approved', to: 'operations_approved', eventType: 'operations_approved' },
  { key: 'order', label: 'Raise the order', from: 'operations_approved', to: 'ordered', eventType: 'ordered' },
  { key: 'close', label: 'Close', from: 'ordered', to: 'closed', eventType: 'closed' },
];

/**
 * Who the current user is relative to a requisition.
 *
 * `canApproveOperations` is the SAME capability that approves a payment
 * voucher, deliberately. Authorising the school to spend money is one
 * authority, and splitting it into two permissions would let somebody
 * approve the commitment while being unable to approve the payment for
 * it — which is not a control, just an obstacle.
 */
export interface ReqActor {
  uid: string;
  /** linkedTeacherId, matched against a requisition's approverId. */
  teacherId?: string;
  canRaise: boolean;
  canApproveOperations: boolean;
  /** May turn an approved requisition into a purchase order. */
  canProcure: boolean;
}

const isRequester = (r: Requisition, a: ReqActor) => !!a.uid && a.uid === r.requestedBy;

const isNamedApprover = (r: Requisition, a: ReqActor) =>
  (!!a.uid && a.uid === r.approverUid)
  || (!!a.teacherId && !!r.approverId && a.teacherId === r.approverId);

/**
 * Why this action is not available, or null if it is.
 *
 * A SENTENCE, NOT A BOOLEAN. "You cannot approve this" leaves somebody
 * clicking; saying which gate stopped them ends the question.
 */
export function reqActionProblem(
  req: Requisition,
  action: ReqAction,
  actor: ReqActor,
): string | null {
  if (action === 'reject') {
    if (REQ_TERMINAL.includes(req.currentStatus)) {
      return 'This requisition is already finished.';
    }
    if (req.currentStatus === 'submitted') {
      return isNamedApprover(req, actor) || actor.canApproveOperations
        ? null
        : 'Only the nominated approver or operations can reject this.';
    }
    return actor.canApproveOperations
      ? null
      : 'Only operations can reject a requisition once it has been approved.';
  }

  const def = REQ_ACTIONS.find((a) => a.key === action);
  if (!def) return 'That is not something that can be done to a requisition.';
  if (req.currentStatus !== def.from) {
    return `A requisition that is "${REQ_STATUS_LABEL[req.currentStatus]}" cannot be ${def.label.toLowerCase()}.`;
  }

  switch (action) {
    case 'submit':
      if (!isRequester(req, actor)) return 'Only whoever raised this may submit it.';
      if ((req.lines ?? []).length === 0) return 'Add at least one item before submitting.';
      if (!req.approverUid && !req.approverId) return 'Nominate somebody to approve this first.';
      return null;

    case 'approverApprove':
      if (!isNamedApprover(req, actor)) {
        return `This is waiting on ${req.approverName ?? 'the nominated approver'}.`;
      }
      /* The distinct-actor rule. Nominating yourself is the obvious way
         round a two-approval chain, and it has to fail here rather than
         be discouraged by the picker. */
      if (isRequester(req, actor)) {
        return 'You raised this requisition, so you cannot also approve it. Nominate somebody else.';
      }
      return null;

    case 'operationsApprove':
      if (!actor.canApproveOperations) {
        return 'Only operations can approve a requisition for purchase.';
      }
      if (isRequester(req, actor)) {
        return 'You raised this requisition, so somebody else in operations must approve it.';
      }
      return null;

    case 'order':
      if (!actor.canProcure) return 'You cannot raise purchase orders.';
      return null;

    case 'close':
      if (!actor.canProcure) return 'You cannot close a requisition.';
      return null;

    default:
      return 'That is not something that can be done to a requisition.';
  }
}

/** The forward actions this user may take right now. */
export function availableReqActions(req: Requisition, actor: ReqActor): ReqActionDef[] {
  return REQ_ACTIONS.filter((a) => reqActionProblem(req, a.key, actor) === null);
}

export const canRejectReq = (req: Requisition, actor: ReqActor): boolean =>
  reqActionProblem(req, 'reject', actor) === null;

/* ================================ money ================================ */

export const reqLineTotal = (l: ReqLine): number =>
  round2((Number(l.qty) || 0) * (Number(l.estUnitCost) || 0));

export const reqEstTotal = (lines: ReqLine[]): number =>
  round2((lines ?? []).reduce((s, l) => s + reqLineTotal(l), 0));

/** Keep the stored line total derived, so a typed figure cannot disagree. */
export function normaliseReqLine(l: ReqLine): ReqLine {
  const qty = Number(l.qty) || 0;
  const estUnitCost = Number(l.estUnitCost) || 0;
  return { ...l, qty, estUnitCost, estLineTotal: round2(qty * estUnitCost) };
}

/* ============================== reporting ============================== */

/** A plain sentence about where it has got to and who is holding it up. */
export function reqStatusSummary(req: Requisition): string {
  switch (req.currentStatus) {
    case 'draft':
      return 'Draft — not yet submitted';
    case 'submitted':
      return `Waiting on ${req.approverName ?? 'the nominated approver'}`;
    case 'approver_approved':
      return `Approved by ${req.approverName ?? 'the department'} · waiting on operations`;
    case 'operations_approved':
      return `Approved${req.operationsApprovedByName ? ` by ${req.operationsApprovedByName}` : ''} · ready to order`;
    case 'ordered':
      return `Ordered${req.poNo ? ` on ${req.poNo}` : ''}`;
    case 'closed':
      return 'Closed';
    case 'rejected':
      return `Rejected${req.rejectedReason ? ` — ${req.rejectedReason}` : ''}`;
    default:
      return REQ_STATUS_LABEL[req.currentStatus];
  }
}

/**
 * What is sitting on somebody's desk, and for how long.
 *
 * A requisition nobody has refused and nobody has approved is the most
 * expensive state in this whole chain: the department believes it is
 * coming, and nothing has been ordered.
 */
export interface ReqWaiting {
  req: Requisition;
  /** Whose gate it is stuck at. */
  waitingOn: string;
  days: number;
}

export function requisitionsWaiting(
  reqs: Requisition[],
  asOf: Date = new Date(),
): ReqWaiting[] {
  const today = asOf.getTime();
  const out: ReqWaiting[] = [];

  for (const r of reqs) {
    let since: string | undefined;
    let waitingOn: string;

    if (r.currentStatus === 'submitted') {
      since = r.submittedAt;
      waitingOn = r.approverName ?? 'the nominated approver';
    } else if (r.currentStatus === 'approver_approved') {
      since = r.approverApprovedAt;
      waitingOn = 'operations';
    } else if (r.currentStatus === 'operations_approved') {
      since = r.operationsApprovedAt;
      waitingOn = 'purchasing';
    } else {
      continue;
    }

    const from = since ? Date.parse(since) : today;
    out.push({
      req: r,
      waitingOn,
      days: Math.max(0, Math.round((today - from) / 86400000)),
    });
  }

  return out.sort((a, b) => b.days - a.days);
}
